import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"
import { Button, Icon, Input, Modal, Popup, Segment, Table } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import api from "api"
import { UserChip } from "components/lib/Chips"
import { UserChooser } from "components/lib/Choosers"
import { useAppSelector } from "store"
import util from "utils/utils"

type PointOfContactRole = {
  id: string
  name: string
  description?: string
  createdAt: string
  updatedAt?: string
  assignedTo?: string
  ownerAssignedTo?: OpenAPI.Schemas["User"]
}

const PointOfContact = () => {
  const [updatingRole, setUpdatingRole] = useState("")
  const user = useAppSelector((state) => state.user)
  const { ownerOrganisation } = user
  const { t } = useTranslation()

  const [pointOfContactRoles, setPointOfContactRoles] = useState<PointOfContactRole[]>([])

  useEffect(() => {
    api.organisations.getPointOfContactRoles(
      ownerOrganisation._id,
      ({ pointOfContactRoles: newRoles }) => {
        setPointOfContactRoles(newRoles)
      },
      () => {},
    )
  }, [ownerOrganisation._id])

  return (
    <Segment>
      <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open={!!updatingRole}>
        <Modal.Header>Update point of contact role</Modal.Header>
        <Modal.Content>
          <h3>Name</h3>
          <p>
            Give this point of contact a descriptive title. This will appear to any user seeking help and will make it
            easier for them to decide who to contact. Make sure this describes the nature of support they should be
            seeking when contacting this user. E.g., "{t("common:capitalise", { key: "generic.idea" })} submissions" or
            "General queries".
          </p>
          <Input
            fluid
            value={pointOfContactRoles.find((r) => r.id === updatingRole)?.name}
            onChange={(_e, { value }) => {
              setPointOfContactRoles(
                pointOfContactRoles.map((r) => {
                  if (r.id === updatingRole) {
                    return {
                      ...r,
                      name: value,
                    }
                  }
                  return r
                }),
              )
            }}
          />
          <h3>Description</h3>
          <p>
            This description will also be shown to the user and gives them further direction of how to ask this point of
            contact for help.
          </p>
          <Input
            fluid
            value={pointOfContactRoles.find((r) => r.id === updatingRole)?.description}
            onChange={(_e, { value }) => {
              setPointOfContactRoles(
                pointOfContactRoles.map((r) => {
                  if (r.id === updatingRole) {
                    return {
                      ...r,
                      description: value as string,
                    }
                  }
                  return r
                }),
              )
            }}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setUpdatingRole("")
            }}
          >
            Cancel
          </Button>
          <Button
            primary
            onClick={() => {
              const role = pointOfContactRoles.find((r) => r.id === updatingRole)
              if (!role) {
                return
              }
              api.organisations.updatePointOfContactRole(
                ownerOrganisation._id,
                role.id,
                {
                  name: role.name,
                  description: role.description,
                },
                ({ pointOfContactRoles: newRoles }) => {
                  setPointOfContactRoles(newRoles)
                  setUpdatingRole("")
                },
                () => {},
              )
            }}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>

      <h3>Point of contact users</h3>
      <p>
        Choose users in your organisation to be a point of contact for support for other users in{" "}
        {ownerOrganisation.name}. For example, someone who can be contacted if a user needs help using the platform.
        Please note that the selected users email addresses will be available to other users in {ownerOrganisation.name}
        .
      </p>

      <Table style={{ maxWidth: "100%" }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell collapsing></Table.HeaderCell>
            <Table.HeaderCell collapsing></Table.HeaderCell>
            <Table.HeaderCell>Role</Table.HeaderCell>
            <Table.HeaderCell>Assigned to</Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
            <Table.HeaderCell>Description</Table.HeaderCell>
            <Table.HeaderCell textAlign="right"></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {pointOfContactRoles.map((role, index) => (
            <Table.Row key={role.id}>
              <Table.Cell textAlign="right">
                {!role.assignedTo ? (
                  <Popup
                    trigger={<Icon name="warning sign" color="yellow" size="big" />}
                    content="This role is currently not visible to your users because it has not been assigned to a user."
                    position="top center"
                  />
                ) : (
                  <Icon name="check circle" color="green" size="big" />
                )}
              </Table.Cell>
              <Table.Cell>
                <div
                  style={{
                    display: "flex",
                  }}
                >
                  <Button
                    disabled={index === 0}
                    size="mini"
                    icon="arrow up"
                    onClick={() => {
                      api.organisations.reorderPointOfContactRole(
                        ownerOrganisation._id,
                        role.id,
                        "up",
                        ({ pointOfContactRoles: newRoles }) => {
                          setPointOfContactRoles(newRoles)
                        },
                        () => {},
                      )
                    }}
                  />
                  <Button
                    disabled={index === pointOfContactRoles.length - 1}
                    size="mini"
                    icon="arrow down"
                    onClick={() => {
                      api.organisations.reorderPointOfContactRole(
                        ownerOrganisation._id,
                        role.id,
                        "down",
                        ({ pointOfContactRoles: newRoles }) => {
                          setPointOfContactRoles(newRoles)
                        },
                        () => {},
                      )
                    }}
                  />
                </div>
              </Table.Cell>
              <Table.Cell collapsing>{role.name}</Table.Cell>
              {role.assignedTo ? (
                <>
                  <Table.Cell collapsing>
                    <UserChip user={role.ownerAssignedTo} />
                  </Table.Cell>
                  <Table.Cell collapsing>
                    <Button
                      size="mini"
                      onClick={() => {
                        util
                          .confirm(
                            "Remove point of contact",
                            `Are you sure you want to remove "${role?.ownerAssignedTo?.profile?.fullName || "Deleted user"}" as the assigned contact for your users if the need help regarding "${role.name}"?`,
                          )
                          .then(() => {
                            api.organisations.unassignPointOfContact(
                              ownerOrganisation._id,
                              role.id,
                              role.assignedTo,
                              ({ pointOfContactRoles: newRoles }) => {
                                setPointOfContactRoles(newRoles)
                              },
                              () => {},
                            )
                          })
                          .catch(() => {})
                      }}
                    >
                      Unassign
                    </Button>
                  </Table.Cell>
                </>
              ) : (
                <Table.Cell colSpan={2} textAlign="right">
                  <UserChooser
                    single
                    trigger={<Button size="mini">Assign user</Button>}
                    enabledFeatures={{ search: true }}
                    searchFunction={(query, success, failure) => {
                      if (!query) {
                        success([])
                        return
                      }
                      api.search.omni(
                        {
                          query,
                          include: ["users"],
                        },
                        ({ users }) => users && success(users),
                        failure,
                      )
                    }}
                    onComplete={(users: OpenAPI.Schemas["User"][]) => {
                      if (users.length === 0) {
                        return
                      }

                      api.organisations.assignPointOfContact(
                        ownerOrganisation._id,
                        role.id,
                        users[0]._id,
                        ({ pointOfContactRoles: newRoles }) => {
                          setPointOfContactRoles(newRoles)
                        },
                        () => {},
                      )
                    }}
                  />
                </Table.Cell>
              )}
              <Table.Cell
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 150,
                }}
              >
                {role.description}
              </Table.Cell>
              <Table.Cell textAlign="right" collapsing>
                <div style={{ display: "flex" }}>
                  <Button
                    size="mini"
                    icon="edit"
                    onClick={() => {
                      setUpdatingRole(role.id)
                    }}
                  />
                  <Button
                    size="mini"
                    icon="trash"
                    basic
                    onClick={() => {
                      util
                        .confirm(
                          "Delete point of contact role",
                          `Are you sure you want to delete the point of contact role "${role.name}"?`,
                        )
                        .then(() => {
                          api.organisations.deletePointOfContactRole(
                            ownerOrganisation._id,
                            role.id,
                            ({ pointOfContactRoles: newRoles }) => {
                              setPointOfContactRoles(newRoles)
                            },
                            () => {},
                          )
                        })
                        .catch(() => {})
                    }}
                  />
                </div>
              </Table.Cell>
            </Table.Row>
          ))}
          <Table.Row>
            <Table.Cell colSpan={7} textAlign="right">
              <Button
                onClick={() => {
                  util
                    .prompt(
                      "Create point of contact role",
                      <>
                        <p>
                          Please give this point of contact a title. This will appear to any user seeking help and will
                          make it easier for them to decide who to contact.
                        </p>
                        <p>
                          Make sure this describes the nature of support they should be seeking when contacting this
                          user. E.g., "{t("common:capitalise", { key: "generic.idea" })} submissions" or "General
                          queries".
                        </p>
                      </>,
                      (roleName) => {
                        api.organisations.createPointOfContactRole(
                          ownerOrganisation._id,
                          { name: roleName },
                          ({ pointOfContactRoles: newRoles }) => {
                            setPointOfContactRoles(newRoles)
                          },
                          () => {},
                        )
                      },
                    )
                    .catch(() => {})
                }}
              >
                Create new role
              </Button>
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>
    </Segment>
  )
}

export default PointOfContact
